import React, { useState } from 'react';
import { Button, Field, Input, Select } from '../../ui/basics';
import { UNITS, beforeError, docName, describeHours, newDoc, toHours } from './docs';

/** A number and its unit as one joined control, e.g. [ 2 | Days ]. */
function SpanFields({ amount, unit, onAmount, onUnit, invalid, label, placeholder }) {
  return (
    <div className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
      <Input type="number" min="1" style={{ width: 96 }} placeholder={placeholder} invalid={invalid} value={amount}
        onChange={(e) => onAmount(e.target.value)} aria-label={label ? label + ' amount' : undefined} />
      <Select className="grow" value={unit} onChange={(e) => onUnit(e.target.value)} options={UNITS} aria-label={label ? label + ' unit' : undefined} />
    </div>
  );
}

/** Add-a-document card: the name, how long the candidate has to provide it, and an optional heads-up for the onboarding executive. */
export function AddDocRow({ onAdd, existing = [], label = 'Add document' }) {
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState('days');
  const [before, setBefore] = useState('');
  const [beforeUnit, setBeforeUnit] = useState('days');
  const [tried, setTried] = useState(false);
  const clean = name.trim();
  const nameError = !clean ? 'Enter a document name.' : clean.length > 100 ? 'Use at most 100 characters.'
    : existing.some((n) => n.trim().toLowerCase() === clean.toLowerCase()) ? 'This document is already on the list.' : '';
  const hours = toHours(amount, unit);
  const amountError = !(Number(amount) > 0) ? 'Enter a number above 0.' : !Number.isInteger(Number(amount)) ? 'Use a whole number.'
    : hours > 24 * 365 ? 'At most one year.' : '';
  const beforeProblem = amountError ? '' : beforeError(before, beforeUnit, hours);
  const hasBefore = String(before).trim() !== '';
  const add = () => {
    setTried(true);
    if (nameError || amountError || beforeProblem) return;
    onAdd(newDoc(name, hours, hasBefore ? toHours(before, beforeUnit) : 0));
    setName('');
    setAmount('');
    setBefore('');
    setTried(false);
  };
  const ready = clean && !amountError && !beforeProblem;
  const summary = ready
    ? '"' + clean + '": the candidate has ' + describeHours(hours) + (hasBefore ? '. The onboarding executive is reminded ' + describeHours(toHours(before, beforeUnit)) + ' before it is due.' : '.')
    : 'Fill in the name and due time to add a document.';
  return (
    <div className="stack" style={{ gap: 14, padding: 16, border: '1px dashed var(--border-strong)', borderRadius: 10, background: 'var(--surface-2)' }}>
      <div style={{ fontWeight: 600 }}>Add a document</div>
      <Field label="Document name" error={tried ? nameError : ''}>
        <Input value={name} invalid={Boolean(tried && nameError)} placeholder="e.g. Driver licence" onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} aria-label="Document" />
      </Field>
      <div className="form-grid">
        <Field label="Due within *" hint="How long the candidate has to provide it." error={tried ? amountError : ''}>
          <SpanFields amount={amount} unit={unit} onAmount={setAmount} onUnit={setUnit} invalid={Boolean(tried && amountError)} label="Due within" />
        </Field>
        <Field label="Remind before due" hint="Optional. Emails the onboarding executive if it is still missing." error={tried ? beforeProblem : ''}>
          <SpanFields amount={before} unit={beforeUnit} onAmount={setBefore} onUnit={setBeforeUnit} invalid={Boolean(tried && beforeProblem)}
            label="Remind before due" placeholder="None" />
        </Field>
      </div>
      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'nowrap', gap: 16 }}>
        <span className="muted" style={{ fontSize: 13 }}>{summary}</span>
        <Button variant="primary" icon="plus" onClick={add} style={{ flex: '0 0 auto' }}>{label}</Button>
      </div>
    </div>
  );
}

/** Editable list of required documents (remove) used when creating candidates and references. */
export function DocList({ docs, onChange }) {
  return (
    <div className="stack" style={{ gap: 8 }}>
      {docs.length === 0 && <p className="muted">No documents yet.</p>}
      {docs.map((doc, i) => (
        <div key={`${docName(doc)}-${i}`} className="row" style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8 }}>
          <b className="grow">{docName(doc)}</b>
          <span className="muted">due within {describeHours(doc.Time)}{doc.Remind_before ? ' · heads-up ' + describeHours(doc.Remind_before) + ' before' : ''}</span>
          <Button variant="ghost" size="sm" icon="x" aria-label={`Remove ${docName(doc)}`} onClick={() => onChange(docs.filter((_, j) => j !== i))} />
        </div>
      ))}
      <AddDocRow onAdd={(doc) => onChange([...docs, doc])} existing={docs.map(docName)} />
    </div>
  );
}
